/**
 * GroupableSortableList - Extends SortableList with velocity-based drag mode switching
 *
 * Adds superset/group creation support via:
 * - Velocity-based mode switching (fast drag = reorder, slow dwell = group)
 * - GroupDropZone rendering when in group mode
 * - onGroupDrop callback when dropping on a group target
 */

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { memo, useCallback, useRef, useState } from 'react';

import { useGroupableDrag } from './useGroupableDrag';

import type {
  GroupableContentProps,
  GroupableSortableListProps,
  SortableItemProps,
} from './GroupableSortableList.types';
import type { SortableItemContext } from './SortableList.types';
import type { DropTarget } from './useGroupableDrag';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';

function SortableItem<T extends { uuid: string; name?: string }>({
  item,
  renderItem,
  disabled,
  isInGroupMode,
  isNearestTarget,
  renderDropZone,
  dropTarget,
}: SortableItemProps<T>): React.JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.uuid, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const context: SortableItemContext = {
    isDragging,
    isOverlay: false,
    handleAttributes: attributes,
    handleListeners: listeners,
  };

  const showDropZone =
    isInGroupMode &&
    isNearestTarget &&
    !isDragging &&
    renderDropZone &&
    dropTarget;

  return (
    <div ref={setNodeRef} style={style}>
      {renderItem(item, context)}
      {showDropZone && renderDropZone(dropTarget, true)}
    </div>
  );
}

/** Inner content component that uses useGroupableDrag (must be inside DndContext) */
function GroupableContent<T extends { uuid: string; name?: string }>({
  items,
  renderItem,
  renderDropZone,
  className,
  disabled,
  groupingEnabled,
  getItemType,
  onDragModeChange,
}: GroupableContentProps<T>): React.JSX.Element {
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());

  const buildTargets = useCallback((): DropTarget[] => {
    return items.map((item) => {
      const element = itemRefs.current.get(item.uuid);
      const rect = element?.getBoundingClientRect();
      const yCenter = rect ? rect.top + rect.height / 2 : 0;

      return {
        uuid: item.uuid,
        name: item.name ?? item.uuid,
        type: getItemType(item),
        yCenter,
      };
    });
  }, [items, getItemType]);

  const { dragMode, nearestTarget, isDragging } = useGroupableDrag({
    targets: buildTargets(),
    enabled: groupingEnabled && !disabled,
  });

  // Notify parent of mode changes for use in handleDragEnd
  const prevModeRef = useRef<'reorder' | 'group'>('reorder');
  const prevTargetRef = useRef<DropTarget | null>(null);
  if (
    dragMode !== prevModeRef.current ||
    nearestTarget?.uuid !== prevTargetRef.current?.uuid
  ) {
    prevModeRef.current = dragMode;
    prevTargetRef.current = nearestTarget;
    onDragModeChange(dragMode, nearestTarget);
  }

  const itemIds = items.map((item) => item.uuid);
  const isInGroupMode = dragMode === 'group' && isDragging;

  return (
    <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
      <div className={className}>
        {items.map((item) => (
          <SortableItem
            key={item.uuid}
            item={item}
            renderItem={renderItem}
            disabled={disabled}
            isInGroupMode={isInGroupMode}
            isNearestTarget={nearestTarget?.uuid === item.uuid}
            renderDropZone={renderDropZone}
            dropTarget={nearestTarget}
          />
        ))}
      </div>
    </SortableContext>
  );
}

function GroupableSortableListInner<T extends { uuid: string; name?: string }>({
  items,
  onReorder,
  onGroupDrop,
  renderItem,
  renderDropZone,
  className = '',
  disabled = false,
  groupingEnabled = true,
  getItemType = () => 'exercise',
}: GroupableSortableListProps<T>): React.JSX.Element {
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeItem = items.find((item) => item.uuid === activeId);

  // Track current drag mode and target from inner component
  const currentModeRef = useRef<'reorder' | 'group'>('reorder');
  const currentTargetRef = useRef<DropTarget | null>(null);

  const handleDragModeChange = useCallback(
    (mode: 'reorder' | 'group', target: DropTarget | null) => {
      currentModeRef.current = mode;
      currentTargetRef.current = target;
    },
    []
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (
        currentModeRef.current === 'group' &&
        currentTargetRef.current &&
        onGroupDrop
      ) {
        onGroupDrop(
          String(active.id),
          currentTargetRef.current.uuid,
          currentTargetRef.current.type
        );
        return;
      }

      if (over && active.id !== over.id) {
        const oldIndex = items.findIndex((item) => item.uuid === active.id);
        const newIndex = items.findIndex((item) => item.uuid === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newItems = [...items];
          const [removed] = newItems.splice(oldIndex, 1);
          newItems.splice(newIndex, 0, removed);
          onReorder(newItems.map((item) => item.uuid));
        }
      }
    },
    [items, onReorder, onGroupDrop]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <GroupableContent
        items={items}
        renderItem={renderItem}
        renderDropZone={renderDropZone}
        className={className}
        disabled={disabled}
        groupingEnabled={groupingEnabled}
        getItemType={getItemType}
        onDragModeChange={handleDragModeChange}
      />
      <DragOverlay>
        {activeItem
          ? renderItem(activeItem, {
              isDragging: true,
              isOverlay: true,
              handleAttributes: undefined,
              handleListeners: undefined,
            })
          : null}
      </DragOverlay>
    </DndContext>
  );
}

export const GroupableSortableList = memo(
  GroupableSortableListInner
) as typeof GroupableSortableListInner;
export default GroupableSortableList;
